import { CallbackTypes } from 'enums/CallbackTypes';
import { PreferredDisplayMode } from 'enums/IncontextDisplayModes';
import { TransactionStates } from 'enums/TransactionStates';
import { Bnpl } from 'services/interfaces/bnpl-data.interface';

interface IOpttyConfigs {
  callbackTypes: Record<keyof typeof CallbackTypes, CallbackTypes>;
  sessionToken: string;
  transactionStates: Record<keyof typeof TransactionStates, TransactionStates>;
}
// eslint-disable-next-line @typescript-eslint/no-unused-vars
declare global {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface Window {
    mw?: (method: string, config: any) => void;
    'JS-Widget'?: string;
    [key: string]: any;
    opttyConfigs: IOpttyConfigs;
  }
}

const loadOpttyInContext = (
  inContextPayload: any,
  sessionToken: string,
  orderService: any,
  widgetToken: string
) => {
  window.opttyConfigs = {
    callbackTypes: CallbackTypes,
    sessionToken,
    transactionStates: TransactionStates,
  };
  window.orderService = orderService;
  let isUserRedirected = false;
  const { /* REACT_APP_API_URL, */ REACT_APP_WIDGET_URL } = process.env;

  const onFinalize = (response: any) => {
    const {
      SUCCESS,
      FAILURE,
      CANCEL,
      NEEDS_VERIFICATION,
    } = window.opttyConfigs.callbackTypes;
    const {
      SUCCESSFUL,
      FAILED,
      DECLINED,
      CANCELED,
      PENDING,
    } = window.opttyConfigs.transactionStates;

    // handling redirection flow
    const handleFinalizedCases = (orderStatus: string) => {
      switch (orderStatus?.toUpperCase()) {
        case SUCCESSFUL: {
          window.finalizedPayload(SUCCESS, response, SUCCESSFUL);
          break;
        }
        case FAILED: {
          window.finalizedPayload(FAILURE, response, FAILED);
          break;
        }
        case DECLINED: {
          window.finalizedPayload(CANCEL, response, DECLINED);
          break;
        }
        case CANCELED: {
          window.finalizedPayload(CANCEL, response, CANCELED);
          break;
        }
        default:
          window.finalizedPayload(NEEDS_VERIFICATION, response, PENDING);
          break;
      }
    };
    handleFinalizedCases(response?.order?.status);
  };

  const onCancel = (cancel: any) => {
    const { CANCEL } = window.opttyConfigs.callbackTypes;
    const { CANCELED } = window.opttyConfigs.transactionStates;
    window.finalizedPayload(CANCEL, cancel, CANCELED);
  };

  const onError = (error: any) => {
    const { ERROR } = window.opttyConfigs.callbackTypes;
    window.finalizedPayload(ERROR, error, window.opttyConfigs.transactionStates.ERROR);
  };

  window.finalizedPayload = (callbackType: string, response: any, status: string) => {
    const redirectUrlWithMetaData = response?.order?.metadataRedirectUrl || response?.metadataRedirectUrl;
    const redirectURL = response?.order?.dynamicRedirectURL || response?.dynamicRedirectURL;
    const hash = response?.order?.hash || response?.hash;
    const orderStatus = response?.order?.status?.toUpperCase() || status?.toUpperCase();
    const payload: any = {
      opttyMetaData: inContextPayload.opttyMetaData,
      reference: inContextPayload.orderReference,
      ref: inContextPayload.orderReference, // fallback for /webhook
      status: orderStatus,
      provider: inContextPayload.bnplProvider,
      bnplProvider: inContextPayload.bnplProvider,
      session_token: sessionToken,
      transactionReference: inContextPayload.transactionReference,
      opttyTransactionReference: inContextPayload.transactionReference,
      callbackType,
      orderToken: inContextPayload?.orderToken,
    };
    if (response?.order?.bnplOrderId) {
      payload.providerReference = response.order.bnplOrderId;
    }
    if (hash) {
      payload.hash = hash;
    }
    const url = hash && redirectURL
        ? redirectURL
        // : redirectUrlWithMetaData || `${REACT_APP_API_URL}/webhook`;
        : redirectUrlWithMetaData || `${window.location.origin}/callback`;
    const finalizedURL = new URL(url);
    Object.entries(payload).forEach(([key, value]: any) => {
      if (value !== undefined && value !== null) {
        finalizedURL.searchParams.set(key, value);
      }
    });
    // redirecting user once, before getting any invalid callback
    if (!isUserRedirected) {
      isUserRedirected = true;
      window.location.href = finalizedURL.toString();
    }
  };

  const script = document.createElement('script');

  script.innerHTML = `
      (function (w, d, s, o, f, js, fjs) {
        w['Optty-Widget-SDK'] = o;
        w[o] = w[o] || function () {
          (w[o].q = w[o].q || []).push(arguments);
        };
        (js = d.createElement(s)), (fjs = d.getElementsByTagName(s)[0]);
        js.id = o;
        js.src = f;
        js.async = 1;
        fjs.parentNode.insertBefore(js, fjs);
      })(window, document, 'script', 'mw', '${REACT_APP_WIDGET_URL}/widget-loader.js');
      mw('init', {
        token:  '${widgetToken}',
        currency: '${inContextPayload?.currency || 'USD'}',
        initialAmount: '${inContextPayload?.orderAmount || 10}',
        location: '${inContextPayload?.location ?? ''}',
        mode: 'live',
      });

      mw('optty-handle-order-payment-widget', {
        token: '${widgetToken}',
        fetchHashAndRedirectURL: 'true',
        payload: ${JSON.stringify(inContextPayload)},
        onCancel: ${onCancel},
        onError: ${onError},
        onFinalize: ${onFinalize}
      });
    `;
  document.body.appendChild(script);
  return null; // This component does not render any UI
};

const isProviderSupportsInContextMode = (bnplConfigs: Bnpl) => {
  const { preferredDisplayMode, incontextDisplayMode } = bnplConfigs;
  const isSupporting = Boolean(preferredDisplayMode && incontextDisplayMode);
  return isSupporting && preferredDisplayMode === PreferredDisplayMode.IN_CONTEXT;
};

export { loadOpttyInContext, isProviderSupportsInContextMode };
